import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';

import { deckKeys } from '@/entities/deck';
import { learningKeys } from '@/entities/learning';
import { BudgetForm } from '@/features/budget';
import { listDecks } from '@/shared/api/decks';
import { getLearningPlan, getLearningStats, getLeeches } from '@/shared/api/learning';
import type { LearningPlan } from '@/shared/api/types';
import { shareOf } from '@/shared/lib/share';
import { currentTimeZone } from '@/shared/lib/timeZone';
import { ErrorMessage, LinkButton } from '@/shared/ui';
import { ActivityHeatmap, StatTiles } from '@/widgets/learning';

import styles from './TodayPage.module.css';

function TodayCard({ plan }: { plan: LearningPlan }): React.JSX.Element {
  const { t } = useTranslation();
  const { today } = plan;
  const budgetMinutes = Math.round(today.budget_seconds / 60);
  const spentMinutes = Math.round(today.spent_seconds / 60);
  const work = today.due_count + today.new_allowed;
  const leftMinutes = Math.max(1, Math.round(today.estimated_seconds / 60));
  const share =
    today.budget_seconds > 0 ? Math.min(today.spent_seconds / today.budget_seconds, 1) : 0;
  const waitReason = today.new_wait_reason === 'none_left' ? '' : today.new_wait_reason;
  const { recovery } = plan;
  const note =
    recovery !== null
      ? recovery.days === 0
        ? t('today.recoveryNoRoom')
        : t('today.recovery', { count: recovery.days, minutes: recovery.minutes_per_day })
      : waitReason !== ''
        ? t(`today.wait.${waitReason}`)
        : '';

  return (
    <section className={styles.card} aria-labelledby="today-budget">
      <div className={styles.budgetRow}>
        <p id="today-budget" className={styles.lead}>
          {t('today.spent', { spent: spentMinutes, budget: budgetMinutes })}
        </p>
        <div
          className={styles.meter}
          role="meter"
          aria-valuemin={0}
          aria-valuemax={budgetMinutes}
          aria-valuenow={Math.min(spentMinutes, budgetMinutes)}
          aria-labelledby="today-budget"
        >
          <span className={styles.meterFill} style={{ width: `${String(share * 100)}%` }} />
        </div>
      </div>

      {work === 0 ? (
        <>
          <p>{t('today.done')}</p>
          {note !== '' && (
            <p className={styles.muted}>
              {note}
              {recovery?.days === 0 && (
                <>
                  {' '}
                  <Link to="/settings">{t('today.changeBudget')}</Link>
                </>
              )}
            </p>
          )}
        </>
      ) : (
        <>
          <p>
            {t('today.work', { due: today.due_count, fresh: today.new_allowed })}{' '}
            <span className={styles.muted}>{t('today.about', { minutes: leftMinutes })}</span>
          </p>
          {note !== '' && (
            <p className={styles.muted}>
              {note}
              {recovery?.days === 0 && (
                <>
                  {' '}
                  <Link to="/settings">{t('today.changeBudget')}</Link>
                </>
              )}
            </p>
          )}
          <LinkButton to="/review/all" variant="primary" className={styles.cta}>
            {t('today.start')}
          </LinkButton>
        </>
      )}
    </section>
  );
}

/**
 * The home of a signed-in user. A newcomer is asked one question — how many
 * minutes a day — and everything after that is planned from the answer.
 */
export function TodayPage(): React.JSX.Element {
  const { t } = useTranslation();
  const timeZone = currentTimeZone();
  const plan = useQuery({
    queryKey: learningKeys.plan(30),
    queryFn: ({ signal }) => getLearningPlan({ timeZone, signal }),
    staleTime: 0,
  });
  const stats = useQuery({
    queryKey: learningKeys.stats,
    queryFn: ({ signal }) => getLearningStats({ timeZone, signal }),
    staleTime: 0,
  });
  const decks = useQuery({ queryKey: deckKeys.all, queryFn: ({ signal }) => listDecks(signal) });
  const leeches = useQuery({
    queryKey: learningKeys.leeches(),
    queryFn: ({ signal }) => getLeeches({ signal }),
    staleTime: 0,
  });

  if (plan.isError) {
    return <ErrorMessage error={plan.error} />;
  }

  if (decks.isError) {
    return <ErrorMessage error={decks.error} />;
  }

  if (!plan.data || !decks.data) {
    return <p className={styles.muted}>{t('common.loading')}</p>;
  }

  const budget = plan.data.settings.daily_minutes;

  if (budget === null) {
    return (
      <div className={styles.page}>
        <h1>{t('today.welcome')}</h1>
        <section className={styles.card} aria-labelledby="onboarding">
          <h2 id="onboarding" className={styles.question}>
            {t('today.askBudget')}
          </h2>
          <p className={styles.muted}>{t('today.askBudgetHint')}</p>
          <BudgetForm
            settings={plan.data.settings}
            submitLabel={t('today.saveBudget')}
            onSaved={() => undefined}
          />
        </section>
      </div>
    );
  }

  return (
    <div className={styles.page}>
      <h1>{t('today.title')}</h1>

      {decks.data.length === 0 ? (
        <section className={styles.card} aria-label={t('today.title')}>
          <p className={styles.lead}>{t('today.noDecks')}</p>
          <LinkButton to="/decks/new" variant="primary" className={styles.cta}>
            {t('today.createDeck')}
          </LinkButton>
        </section>
      ) : (
        <TodayCard plan={plan.data} />
      )}

      {leeches.data && leeches.data.leeches.length > 0 && leeches.data.leech_seconds > 0 && (
        <p className={styles.health}>
          {t('health.todayLine', {
            count: leeches.data.leeches.length,
            share: shareOf(leeches.data.leech_seconds, leeches.data.total_seconds),
          })}{' '}
          <Link to="/health">{t('health.fix')}</Link>
        </p>
      )}

      {decks.data.length > 0 && stats.data && (
        <>
          <section className={styles.section} aria-labelledby="activity-title">
            <h2 id="activity-title">{t('dashboard.activity')}</h2>
            <ActivityHeatmap days={stats.data.days} since={stats.data.since} />
          </section>
          <section className={styles.section} aria-labelledby="progress-title">
            <h2 id="progress-title">{t('dashboard.progress')}</h2>
            <StatTiles stats={stats.data} />
          </section>
        </>
      )}
    </div>
  );
}
